import { Box, ListItemText } from "@mui/material";
import {
  Field,
  FieldInputProps,
  FieldMetaProps,
  useFormikContext,
} from "formik";
import { ReactElement, useCallback } from "react";

import {
  GetNgpVanSavedListQuery,
  GetNgpVanSavedListQueryVariables,
  GetNgpVanSavedListsQuery,
  GetNgpVanSavedListsQueryVariables,
  NgpVanDatabaseModeGqlType,
  NgpVanObjectType,
  NgpVanSavedListFieldsFragment,
} from "~frontend/__generated__/graphql";
import {
  GET_NGPVAN_SAVED_LIST,
  GET_NGPVAN_SAVED_LISTS,
} from "~frontend/graphql/integration";
import { palette } from "~frontend/theme";

import NgpVanObjectAutocomplete from "./NgpVanObjectAutocomplete";
import { BaseProps } from "./types";

const AUTO_REFRESH_SAVED_LISTS = 1000 * 60 * 15; // 15 minutes

export function NgpVanSavedListAutocomplete({
  onChange,
  ...baseProps
}: BaseProps & {
  onChange: (
    newValue: number | undefined,
    newListSize: number | undefined
  ) => void;
}): ReactElement {
  const { integrationId, databaseMode } = baseProps;

  return NgpVanObjectAutocomplete<
    NgpVanSavedListFieldsFragment,
    GetNgpVanSavedListQuery,
    GetNgpVanSavedListQueryVariables,
    GetNgpVanSavedListsQuery,
    GetNgpVanSavedListsQueryVariables
  >({
    ...baseProps,
    objectType: NgpVanObjectType.SavedLists,
    autoRefreshWindowMillis: AUTO_REFRESH_SAVED_LISTS,
    noResultsLabel: "No Saved Lists Found",
    errorLabel: "Error Loading Saved Lists",
    onChange: newList => {
      onChange(
        newList?.savedListId ?? undefined,
        newList?.listCount ?? undefined
      );
    },
    getLastRefreshTime: useCallback(
      integration => {
        const maybeTimestamp =
          databaseMode === NgpVanDatabaseModeGqlType.MyVoters
            ? integration.ngpVanMyVotersSavedListLastRefresh
            : integration.ngpVanMyCampaignSavedListLastRefresh;

        return maybeTimestamp ? new Date(maybeTimestamp) : null;
      },
      [databaseMode]
    ),
    getLastRefreshJob: useCallback(
      integration => {
        return (
          (databaseMode === NgpVanDatabaseModeGqlType.MyVoters
            ? integration.ngpVanMyVotersSavedListRefreshJob
            : integration.ngpVanMyCampaignSavedListRefreshJob) ?? null
        );
      },
      [databaseMode]
    ),

    getObjectQuery: GET_NGPVAN_SAVED_LIST,
    getObjectQueryVariables: useCallback(
      value => ({
        input: {
          integrationId,
          databaseMode,
          savedListId: value,
        },
      }),
      [integrationId, databaseMode]
    ),
    getObjectQueryResultToObject: useCallback(
      data => data.getNgpVanSavedList ?? null,
      []
    ),
    objectToGetObjectQueryResult: useCallback(
      savedList => ({
        getNgpVanSavedList: savedList,
      }),
      []
    ),

    listObjectQuery: GET_NGPVAN_SAVED_LISTS,
    listObjectQueryVariables: useCallback(
      nameContains => ({
        input: {
          integrationId,
          databaseMode,
          nameContains: nameContains || null,
        },
      }),
      [integrationId, databaseMode]
    ),
    listObjectQueryResultToObjects: useCallback(
      data => data.getNgpVanSavedLists,
      []
    ),

    searchTextFromObject: useCallback(savedList => savedList.name, []),
    valueFromObject: useCallback(savedList => savedList.savedListId, []),
    listItemFromObject: useCallback(
      savedList => (
        <ListItemText
          primary={savedList.name}
          primaryTypographyProps={{ component: "div" }}
          secondary={
            <Box sx={{ fontSize: "12px", color: palette.navy }}>
              <strong>{savedList.listCount} contacts</strong>
              {savedList.description ? (
                <>
                  {" - "}
                  <i>{savedList.description}</i>
                </>
              ) : null}
            </Box>
          }
          secondaryTypographyProps={{ component: "div" }}
        />
      ),
      []
    ),
  });
}

export function FormikNgpVanSavedListAutocomplete({
  label,
  name,
  listCountName,
  integrationId,
  databaseMode,
  readOnly,
}: {
  label: string;
  name: string;
  listCountName: string;
  integrationId: string;
  databaseMode: NgpVanDatabaseModeGqlType;
  readOnly?: boolean;
}): ReactElement {
  const formik = useFormikContext<any>();

  return (
    <Field name={name}>
      {({
        field,
        meta,
      }: {
        field: FieldInputProps<unknown>;
        meta: FieldMetaProps<unknown>;
      }) => (
        <NgpVanSavedListAutocomplete
          label={label}
          integrationId={integrationId}
          databaseMode={databaseMode}
          readOnly={readOnly}
          value={field.value as number}
          onChange={async (newValue, newListSize) => {
            // last argument is a boolean for whether to re-validate the form--
            // we only do that on the last call
            await formik.setFieldValue(name, newValue, false);
            await formik.setFieldTouched(name, true, false);

            await formik.setFieldValue(listCountName, newListSize, false);
            await formik.setFieldTouched(listCountName, true, true);
          }}
          error={Boolean(
            meta.touched && (meta.error || formik.errors[listCountName])
          )}
          helperText={
            meta.touched
              ? meta.error || formik.errors[listCountName]?.toString() || ""
              : null
          }
        />
      )}
    </Field>
  );
}
